import classNames from 'classnames';
import throttle from 'lodash/throttle';
import { useState, useEffect, useRef, forwardRef } from 'react';
import { ScrollTo } from 'react-scroll-to';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

import BelowMinimumCardRedesign from 'components/common/BelowMinimumCardRedesign';
import LoadingCard from 'components/common/LoadingCard';
import LowShippingCard from 'components/common/LowShippingCard';
import ScrollArrows from 'components/common/ScrollArrows';
import * as Routes from 'helpers/routes';
import { sumStyleVars } from 'helpers/styleVariables';
import useScreenSizeConstants from 'hooks/useScreenSizeConstants';
import mealCardStyle from 'styles/shared/dashboardMealCard';
import mealCardRedesignStyle from 'styles/shared/dashboardMealCardRedesign';
import styleVars from 'styles/shared/jsVariables';
import * as Types from 'utilities/types';

const scrollWidthDesktop = sumStyleVars(styleVars.mealCardWidthDesktop, styleVars.mealCardPadding);
const scrollWidthMobile = sumStyleVars(styleVars.mealCardWidthMobile, styleVars.mealCardPadding);
const scrollWidthCompressed = sumStyleVars(
  styleVars.mealCardWidthCompressed,
  styleVars.mealCardPadding
);

const THROTTLE_MS = 300;

const MealCardsList = forwardRef(
  (
    {
      allowScroll = true,
      compressMealCard = false,
      meals,
      mealCardComponent: MealCardComponent,
      mealOptionSelections = {},
      menuSlug,
      showBelowMinimum = false,
      favorites = {},
      displayLowShippingCard = false,
      shippingPriceDiffAmountCentsFormatted,
      isSkippedBasket = false,
      linkPath,
    },
    ref
  ) => (
    <ul
      ref={ref}
      className={classNames(
        'list-none',
        'm-0',
        'px-4',
        'pb-6',
        'pt-4',
        'bp2:px-8',
        'flex',
        'bp2:min-h-[154px]',
        {
          ['scrolling-touch overflow-x-scroll scrollbar-hidden']: allowScroll,
          ['overflow-x-hidden']: !allowScroll,
        }
      )}
    >
      {showBelowMinimum ? (
        <li className="pr-4">
          <BelowMinimumCardRedesign
            actionLink={Routes.dashboardMenuPath(menuSlug)}
            cardStyle={classNames(mealCardStyle.mealCard, mealCardRedesignStyle.belowMinimumCard)}
          />
        </li>
      ) : (
        <>
          {meals.map((meal, i) => (
            <li
              className={classNames(i < meals.length - 1 && 'pr-4 last:pr-6 bp2:last:pr-8')}
              key={meal.id}
            >
              <MealCardComponent
                meal={meal}
                mealOptionSelection={mealOptionSelections[meal.id] || []}
                menuSlug={menuSlug}
                compressMealCard={compressMealCard}
                isFavorite={Boolean(favorites[meal.mealConcept])}
                isSkippedBasket={isSkippedBasket}
                linkPath={linkPath}
              />
            </li>
          ))}
          {displayLowShippingCard && (
            <li key="shippingCard" className="px-4 last:pr-6 bp2:last:pr-8">
              <LowShippingCard priceDiff={shippingPriceDiffAmountCentsFormatted} />
            </li>
          )}
        </>
      )}
    </ul>
  )
);

MealCardsList.propTypes = {
  allowScroll: PropTypes.bool,
  compressMealCard: PropTypes.bool,
  displayLowShippingCard: PropTypes.bool,
  meals: PropTypes.arrayOf(Types.meal).isRequired,
  mealOptionSelections: PropTypes.object,
  menuSlug: PropTypes.string.isRequired,
  showBelowMinimum: PropTypes.bool,
  favorites: PropTypes.any,
  mealCardComponent: PropTypes.any,
  shippingPriceDiffAmountCentsFormatted: PropTypes.any,
  isSkippedBasket: PropTypes.bool,
  linkPath: PropTypes.func,
};

const ScrollableMealCardsList = ({
  meals = [],
  compressMealCard = false,
  isAtLeastBp2,
  transitionRef,
  ...props
}) => {
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const updateShowState = throttle(() => {
      if (containerRef.current) {
        const { offsetWidth, scrollWidth, scrollLeft } = containerRef.current;
        setShowPrev(scrollLeft > 0);
        setShowNext(scrollLeft + offsetWidth < scrollWidth);
      }
    }, THROTTLE_MS);

    updateShowState();

    window.addEventListener('resize', updateShowState);
    containerRef.current?.addEventListener('scroll', updateShowState);

    return () => {
      window.removeEventListener('resize', updateShowState);
      containerRef.current?.removeEventListener('scroll', updateShowState);
      updateShowState.cancel();
    };
  }, [meals.length]);

  const getMealCardWidth = () => {
    if (compressMealCard) return scrollWidthCompressed;
    if (isAtLeastBp2) return scrollWidthDesktop;
    return scrollWidthMobile;
  };

  const scrollValue = () => {
    const mealCardWidth = getMealCardWidth();
    return Math.floor(containerRef.current.offsetWidth / mealCardWidth) * mealCardWidth;
  };

  const updateScrollPosition = scrollChange => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        left: scrollChange,
        behavior: 'smooth',
      });
    }
  };

  const scrollLeft = () => {
    updateScrollPosition(-scrollValue());
  };

  const scrollRight = () => {
    updateScrollPosition(scrollValue());
  };

  return (
    <div ref={transitionRef}>
      <MealCardsList
        {...props}
        meals={meals}
        allowScroll
        ref={containerRef}
        compressMealCard={compressMealCard}
      />
      <ScrollArrows
        scrollLeft={scrollLeft}
        scrollRight={scrollRight}
        showPrev={showPrev}
        showNext={showNext}
      />
    </div>
  );
};

ScrollableMealCardsList.propTypes = {
  meals: PropTypes.array.isRequired,
  scrollTo: PropTypes.func.isRequired,
  isAtLeastBp2: PropTypes.bool.isRequired,
  compressMealCard: PropTypes.bool,
  transitionRef: PropTypes.object,
};

const MealCardsContainer = ({ showLoading, showMeals, ...restProps }) => {
  const { isAtLeastBp2 } = useScreenSizeConstants();
  const loadingRef = useRef();
  const listRef = useRef();

  return (
    <>
      {showLoading && <span className="sr">Loading meals...</span>}
      <TransitionGroup className="relative">
        {showLoading && (
          <CSSTransition timeout={200} classNames="fadeInOut" nodeRef={loadingRef}>
            <ul
              ref={loadingRef}
              aria-hidden
              className="m-0 flex min-h-[360px] list-none overflow-hidden pb-4 pl-6 bp2:pl-8"
            >
              {[1, 2, 3].map(n => (
                <li key={n} className={classNames({ 'pr-6 bp2:pr-8': n === 3, 'pr-4': n !== 3 })}>
                  <LoadingCard />
                </li>
              ))}
            </ul>
          </CSSTransition>
        )}
        {!showLoading && (
          <CSSTransition timeout={200} classNames="fadeInOut" nodeRef={listRef}>
            <CSSTransition
              in={showMeals}
              timeout={500}
              classNames={{
                enter: 'fadeInOut-enter scaleHeight-enter',
                enterActive: 'fadeInOut-enter-active scaleHeight-enter-active',
                exit: 'fadeInOut-exit scaleHeight-exit',
                exitActive: 'fadeInOut-exit-active scaleHeight-exit-active',
              }}
              unmountOnExit
              nodeRef={listRef}
            >
              <ScrollTo>
                {scrollProps => (
                  <ScrollableMealCardsList
                    {...scrollProps}
                    {...restProps}
                    transitionRef={listRef}
                    isAtLeastBp2={isAtLeastBp2}
                  />
                )}
              </ScrollTo>
            </CSSTransition>
          </CSSTransition>
        )}
      </TransitionGroup>
    </>
  );
};

MealCardsContainer.propTypes = {
  showLoading: PropTypes.bool.isRequired,
  showMeals: PropTypes.bool.isRequired,
};

export default MealCardsContainer;
